import React, { useState, useMemo } from 'react';
import {
  AlertTriangle, Target, Shield, Zap, ChevronDown, ChevronRight, ChevronUp,
  Server, Globe, Network, Lock, Key, Database, Crown, Eye, Bug, Skull,
  TrendingUp, Layers, Link2, ArrowRight, AlertCircle, CheckCircle,
  Crosshair, Activity, Brain, Sparkles, Loader2, Info, BarChart3, PieChart,
  List, LayoutGrid, Filter
} from 'lucide-react';
import { useI18n } from '../i18n';

// Calculate attack chain score for a host with multiple vulnerabilities
const calculateChainScore = (vulns, t) => {
  if (!vulns || vulns.length === 0) return { score: 0, rating: 'safe', factors: [] };

  let baseScore = 0;
  let factors = [];

  const severityScores = { critical: 40, high: 25, medium: 10, low: 5, info: 1 };
  const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };

  vulns.forEach(v => {
    const sev = v.severity?.toLowerCase() || 'info';
    counts[sev] = (counts[sev] || 0) + 1;
    baseScore += severityScores[sev] || 0;
  });

  const vulnCount = vulns.length;
  if (vulnCount >= 5) {
    baseScore *= 1.5;
    factors.push({ type: 'multi_vuln', label: t('vulnerabilities.attackChain.factors.multiVuln5', { count: vulnCount }) || `${vulnCount} vulns combinables`, impact: '+50%', color: 'red' });
  } else if (vulnCount >= 3) {
    baseScore *= 1.3;
    factors.push({ type: 'multi_vuln', label: t('vulnerabilities.attackChain.factors.multiVuln3', { count: vulnCount }) || `${vulnCount} vulns on this host`, impact: '+30%', color: 'orange' });
  }

  if (counts.critical > 0 && counts.high > 0) {
    baseScore *= 1.4;
    factors.push({ type: 'combo_critical_high', label: t('vulnerabilities.attackChain.factors.criticalHighCombo') || 'Combo Critique + Haute', impact: '+40%', color: 'red' });
  }

  const hasRCE = vulns.some(v => v.title?.toLowerCase().includes('remote code') || v.title?.toLowerCase().includes('rce') || v.description?.toLowerCase().includes('remote code execution') || v.cvss_score >= 9.0);
  if (hasRCE) {
    baseScore *= 1.5;
    factors.push({ type: 'rce_potential', label: t('vulnerabilities.attackChain.factors.rce') || 'RCE potentiel', impact: '+50%', color: 'red' });
  }

  const hasAuthBypass = vulns.some(v => v.title?.toLowerCase().includes('bypass') || v.title?.toLowerCase().includes('authentication') || v.description?.toLowerCase().includes('bypass authentication'));
  if (hasAuthBypass) {
    baseScore *= 1.3;
    factors.push({ type: 'auth_bypass', label: t('vulnerabilities.attackChain.factors.authBypass') || 'Bypass auth possible', impact: '+30%', color: 'orange' });
  }

  const hasPrivEsc = vulns.some(v => v.title?.toLowerCase().includes('privilege') || v.title?.toLowerCase().includes('escalation') || v.description?.toLowerCase().includes('privilege escalation'));
  if (hasPrivEsc) {
    baseScore *= 1.3;
    factors.push({ type: 'priv_esc', label: t('vulnerabilities.attackChain.factors.privEsc') || 'Privilege escalation', impact: '+30%', color: 'orange' });
  }

  const hasExposedService = vulns.some(v => v.port === 22 || v.port === 23 || v.port === 3389 || v.port === 445 || v.port === 21 || v.port === 80 || v.port === 443);
  if (hasExposedService) {
    baseScore *= 1.2;
    factors.push({ type: 'exposed', label: t('vulnerabilities.attackChain.factors.exposed') || 'Exposed services', impact: '+20%', color: 'yellow' });
  }

  const avgCVSS = vulns.reduce((sum, v) => sum + (v.cvss_score || 0), 0) / vulns.length;
  if (avgCVSS >= 8) {
    baseScore *= 1.2;
    factors.push({ type: 'high_cvss', label: t('vulnerabilities.attackChain.factors.highCvss', { score: avgCVSS.toFixed(1) }) || `CVSS moyen ${avgCVSS.toFixed(1)}`, impact: '+20%', color: 'red' });
  }

  const finalScore = Math.min(100, Math.round(baseScore));
  let rating;
  if (finalScore >= 80) rating = 'critical';
  else if (finalScore >= 60) rating = 'high';
  else if (finalScore >= 40) rating = 'medium';
  else if (finalScore >= 20) rating = 'low';
  else rating = 'safe';

  return { score: finalScore, rating, factors };
};

// Calculate attack path
const calculateAttackPath = (hostVulns, t) => {
  const path = [];
  const vulns = [...hostVulns].sort((a, b) => {
    const orderScore = (v) => {
      const title = v.title?.toLowerCase() || '';
      if (title.includes('initial') || title.includes('remote code') || title.includes('rce')) return 1;
      if (title.includes('authentication') || title.includes('bypass')) return 2;
      if (title.includes('privilege') || title.includes('escalation')) return 3;
      if (title.includes('lateral') || title.includes('pivot')) return 4;
      if (title.includes('data') || title.includes('exfiltration') || title.includes('database')) return 5;
      return 3;
    };
    return orderScore(a) - orderScore(b);
  });

  if (vulns.length > 0) {
    const initialVuln = vulns.find(v => v.cvss_score >= 7 || v.severity === 'critical' || v.severity === 'high') || vulns[0];
    path.push({ stage: 'initial_access', label: t('vulnerabilities.attackChain.stages.initialAccess') || 'Initial Access', icon: 'Crosshair', color: 'red', vuln: initialVuln, description: `${t('vulnerabilities.attackChain.exploitVia') || 'Exploitation via'} ${initialVuln.title?.substring(0, 40)}...` });

    if (vulns.length >= 2) {
      const privVuln = vulns.find(v => v.title?.toLowerCase().includes('privilege') || v.title?.toLowerCase().includes('escalation') || (v !== initialVuln && v.severity === 'critical')) || vulns[1];
      path.push({ stage: 'privilege_escalation', label: t('vulnerabilities.attackChain.stages.privEscalation') || 'Privilege Escalation', icon: 'TrendingUp', color: 'orange', vuln: privVuln, description: `${t('vulnerabilities.attackChain.escalationVia') || 'Escalation via'} ${privVuln.title?.substring(0, 40)}...` });
    }

    if (vulns.length >= 3) {
      const lateralVuln = vulns.find(v => v.title?.toLowerCase().includes('smb') || v.title?.toLowerCase().includes('rdp') || v.title?.toLowerCase().includes('ssh') || (v !== vulns[0] && v !== vulns[1])) || vulns[2];
      path.push({ stage: 'persistence', label: t('vulnerabilities.attackChain.stages.persistence') || 'Persistance', icon: 'Layers', color: 'purple', vuln: lateralVuln, description: `${t('vulnerabilities.attackChain.maintenanceVia') || 'Maintien via'} ${lateralVuln.title?.substring(0, 40)}...` });
    }

    path.push({
      stage: 'objective', label: t('vulnerabilities.attackChain.stages.objective') || 'Objectif', icon: 'Crown', color: 'yellow', vuln: null,
      description: vulns.length >= 3 ? t('vulnerabilities.attackChain.fullCompromise') || 'Full compromise' : vulns.length >= 2 ? t('vulnerabilities.attackChain.adminAccess') || 'Admin access possible' : t('vulnerabilities.attackChain.initialSystemAccess') || 'Initial access'
    });
  }
  return path;
};

// ============== TAB: OVERVIEW (Stats + Chart compact) ==============
const OverviewTab = ({ vulnerabilities, stats, t }) => {
  const severityCounts = useMemo(() => ({
    critical: vulnerabilities.filter(v => v.severity === 'critical').length,
    high: vulnerabilities.filter(v => v.severity === 'high').length,
    medium: vulnerabilities.filter(v => v.severity === 'medium').length,
    low: vulnerabilities.filter(v => v.severity === 'low').length,
    info: vulnerabilities.filter(v => v.severity === 'info').length
  }), [vulnerabilities]);

  const avgCvss = useMemo(() => {
    const total = vulnerabilities.length;
    return total > 0 ? (vulnerabilities.reduce((sum, v) => sum + (v.cvss_score || 0), 0) / total).toFixed(1) : '0.0';
  }, [vulnerabilities]);

  const topCves = useMemo(() => [...vulnerabilities].filter(v => v.cve_id).sort((a, b) => (b.cvss_score || 0) - (a.cvss_score || 0)).slice(0, 5), [vulnerabilities]);
  const maxCount = Math.max(...Object.values(severityCounts), 1);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      {/* Stats Cards */}
      <div className="xl:col-span-1">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-center">
            <Skull className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-red-400">{stats.criticalPaths}</div>
            <div className="text-xs text-gray-400">{t('vulnerabilities.attackChain.criticalPaths') || 'Chemins Critiques'}</div>
          </div>
          <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3 text-center">
            <AlertTriangle className="w-5 h-5 text-orange-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-orange-400">{stats.highRiskPaths}</div>
            <div className="text-xs text-gray-400">{t('vulnerabilities.attackChain.highRisk') || 'High Risk'}</div>
          </div>
          <div className="bg-brand-green/20 border border-brand-green/30 rounded-lg p-3 text-center">
            <Activity className="w-5 h-5 text-brand-green mx-auto mb-1" />
            <div className="text-2xl font-bold text-brand-green">{avgCvss}</div>
            <div className="text-xs text-gray-400">{t('vulnerabilities.attackChain.avgCvss') || 'CVSS Moyen'}</div>
          </div>
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-center">
            <Server className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-blue-400">{stats.totalHosts}</div>
            <div className="text-xs text-gray-400">{t('vulnerabilities.attackChain.hostsAnalyzed') || 'Hosts'}</div>
          </div>
        </div>
      </div>

      {/* Severity Distribution Chart */}
      <div className="xl:col-span-1 bg-gray-800/30 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          {t('vulnerabilities.attackChain.bySeverity') || 'By Severity'}
        </h4>
        <div className="space-y-2">
          {[
            { key: 'critical', labelKey: 'vulnerabilities.severities.critical', color: 'bg-red-500', textColor: 'text-red-400' },
            { key: 'high', labelKey: 'vulnerabilities.severities.high', color: 'bg-orange-500', textColor: 'text-orange-400' },
            { key: 'medium', labelKey: 'vulnerabilities.severities.medium', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
            { key: 'low', labelKey: 'vulnerabilities.severities.low', color: 'bg-blue-500', textColor: 'text-blue-400' }
          ].map(item => (
            <div key={item.key} className="flex items-center gap-2">
              <span className={`w-16 text-xs ${item.textColor}`}>{t(item.labelKey)}</span>
              <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${(severityCounts[item.key] / maxCount) * 100}%` }} />
              </div>
              <span className="w-8 text-xs text-white font-bold text-right">{severityCounts[item.key]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top CVEs */}
      <div className="xl:col-span-1 bg-gray-800/30 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
          <Bug className="w-4 h-4" />
          {t('vulnerabilities.attackChain.topCves') || 'Top CVE'}
        </h4>
        {topCves.length > 0 ? (
          <div className="space-y-1.5">
            {topCves.map((cve, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs p-2 bg-gray-800/50 rounded">
                <span className="text-brand-green font-mono truncate flex-1">{cve.cve_id}</span>
                <span className={`ml-2 px-1.5 py-0.5 rounded font-bold ${cve.cvss_score >= 9 ? 'bg-red-500/30 text-red-400' : cve.cvss_score >= 7 ? 'bg-orange-500/30 text-orange-400' : 'bg-yellow-500/30 text-yellow-400'}`}>
                  {cve.cvss_score?.toFixed(1) || 'N/A'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-xs text-center py-4">{t('vulnerabilities.noCVE') || 'Aucun CVE'}</p>
        )}
      </div>
    </div>
  );
};

// ============== TAB: ATTACK CHAINS (Ranked hosts) ==============
const AttackChainsTab = ({ rankedHosts, onVulnClick, t }) => {
  const [expandedHost, setExpandedHost] = useState(null);

  const getRatingColor = (rating) => {
    const colors = { critical: 'border-red-500 bg-red-900/20', high: 'border-orange-500 bg-orange-900/20', medium: 'border-yellow-500 bg-yellow-900/20', low: 'border-blue-500 bg-blue-900/20', safe: 'border-green-500 bg-green-900/20' };
    return colors[rating] || colors.safe;
  };

  const getRatingBadge = (rating) => {
    const badges = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-yellow-500', low: 'bg-blue-500', safe: 'bg-green-500' };
    return badges[rating] || badges.safe;
  };

  const getIconComponent = (iconName) => {
    const icons = { Crosshair, TrendingUp, Layers, Crown, Target, Shield };
    return icons[iconName] || Target;
  };

  return (
    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
      {rankedHosts.slice(0, 15).map((hostData, idx) => {
        const isExpanded = expandedHost === hostData.host;
        const attackPath = calculateAttackPath(hostData.vulns, t);
        const severityCounts = {
          critical: hostData.vulns.filter(v => v.severity === 'critical').length,
          high: hostData.vulns.filter(v => v.severity === 'high').length,
          medium: hostData.vulns.filter(v => v.severity === 'medium').length,
          low: hostData.vulns.filter(v => v.severity === 'low').length
        };

        return (
          <div key={hostData.host} className={`border rounded-lg overflow-hidden transition-all ${getRatingColor(hostData.rating)}`}>
            {/* Compact Header */}
            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5" onClick={() => setExpandedHost(isExpanded ? null : hostData.host)}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${idx < 3 ? getRatingBadge(hostData.rating) + ' text-white' : 'bg-gray-700 text-gray-300'}`}>
                  #{idx + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-gray-400" />
                    <span className="text-white font-medium text-sm">{hostData.host}</span>
                    {hostData.rating === 'critical' && <Skull className="w-3 h-3 text-red-400 animate-pulse" />}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-500">{hostData.vulns.length} vulns</span>
                    <div className="flex gap-1">
                      {severityCounts.critical > 0 && <span className="px-1 py-0.5 bg-red-500/30 text-red-400 rounded text-[10px]">{severityCounts.critical}C</span>}
                      {severityCounts.high > 0 && <span className="px-1 py-0.5 bg-orange-500/30 text-orange-400 rounded text-[10px]">{severityCounts.high}H</span>}
                      {severityCounts.medium > 0 && <span className="px-1 py-0.5 bg-yellow-500/30 text-yellow-400 rounded text-[10px]">{severityCounts.medium}M</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className={`text-xl font-bold ${hostData.rating === 'critical' ? 'text-red-400' : hostData.rating === 'high' ? 'text-orange-400' : hostData.rating === 'medium' ? 'text-yellow-400' : 'text-green-400'}`}>
                    {hostData.score}
                  </span>
                  <span className="text-gray-500 text-xs">/100</span>
                </div>
                {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-gray-700 p-4 bg-brand-black/50">
                {/* Attack Path */}
                <div className="mb-4">
                  <h5 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    {t('vulnerabilities.attackChain.attackPath') || 'Chemin d\'attaque'}
                  </h5>
                  <div className="flex items-center gap-1 overflow-x-auto pb-2">
                    {attackPath.map((step, i) => {
                      const IconComp = getIconComponent(step.icon);
                      return (
                        <React.Fragment key={i}>
                          <div className={`flex-shrink-0 p-2 rounded-lg border text-xs ${step.color === 'red' ? 'bg-red-900/30 border-red-500/50' : step.color === 'orange' ? 'bg-orange-900/30 border-orange-500/50' : step.color === 'purple' ? 'bg-purple-900/30 border-purple-500/50' : 'bg-yellow-900/30 border-yellow-500/50'}`}>
                            <div className="flex items-center gap-1 mb-0.5">
                              <IconComp className={`w-3 h-3 ${step.color === 'red' ? 'text-red-400' : step.color === 'orange' ? 'text-orange-400' : step.color === 'purple' ? 'text-purple-400' : 'text-yellow-400'}`} />
                              <span className="text-white font-medium">{step.label}</span>
                            </div>
                            <p className="text-gray-400 text-[10px] max-w-[120px] truncate">{step.description}</p>
                          </div>
                          {i < attackPath.length - 1 && <ArrowRight className="w-4 h-4 text-red-500 flex-shrink-0" />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Vulnerabilities List */}
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {hostData.vulns.sort((a, b) => {
                    const sevOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
                    return (sevOrder[a.severity] || 4) - (sevOrder[b.severity] || 4);
                  }).slice(0, 5).map((vuln, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-800/30 rounded-lg cursor-pointer hover:bg-gray-800/50" onClick={() => onVulnClick && onVulnClick(vuln)}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${vuln.severity === 'critical' ? 'bg-red-500 text-white' : vuln.severity === 'high' ? 'bg-orange-500 text-white' : vuln.severity === 'medium' ? 'bg-yellow-500 text-black' : 'bg-blue-500 text-white'}`}>
                          {vuln.severity?.substring(0, 1).toUpperCase()}
                        </span>
                        <span className="text-white text-xs truncate">{vuln.title}</span>
                      </div>
                      {vuln.cve_id && <span className="text-brand-green text-[10px] font-mono ml-2">{vuln.cve_id}</span>}
                    </div>
                  ))}
                  {hostData.vulns.length > 5 && (
                    <p className="text-center text-gray-500 text-xs py-1">+{hostData.vulns.length - 5} {t('common.more') || 'de plus'}</p>
                  )}
                </div>

                {/* Risk Factors */}
                {hostData.factors?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-700">
                    {hostData.factors.map((factor, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] flex items-center gap-1 ${factor.color === 'red' ? 'bg-red-500/20 text-red-400' : factor.color === 'orange' ? 'bg-orange-500/20 text-orange-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        <Zap className="w-2 h-2" />
                        {factor.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============== MAIN COMPONENT ==============
const AttackChainRanking = ({ vulnerabilities = [], onVulnClick }) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('overview');

  const rankedHosts = useMemo(() => {
    const hostGroups = {};
    vulnerabilities.forEach(vuln => {
      const host = vuln.host_id || vuln.scan_target || vuln.target || 'unknown';
      if (!hostGroups[host]) hostGroups[host] = [];
      hostGroups[host].push(vuln);
    });

    return Object.entries(hostGroups)
      .map(([host, vulns]) => ({ host, vulns, ...calculateChainScore(vulns, t) }))
      .filter(h => h.vulns.length >= 1)
      .sort((a, b) => b.score - a.score);
  }, [vulnerabilities, t]);

  const stats = useMemo(() => ({
    criticalPaths: rankedHosts.filter(h => h.rating === 'critical').length,
    highRiskPaths: rankedHosts.filter(h => h.rating === 'high').length,
    multiVulnHosts: rankedHosts.filter(h => h.vulns.length >= 3).length,
    avgScore: rankedHosts.length > 0 ? Math.round(rankedHosts.reduce((sum, h) => sum + h.score, 0) / rankedHosts.length) : 0,
    totalHosts: rankedHosts.length
  }), [rankedHosts]);

  if (rankedHosts.length === 0) {
    return (
      <div className="bg-brand-black rounded-xl border border-gray-700 p-8 text-center">
        <Shield className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-white mb-1">{t('vulnerabilities.attackChain.noChains') || 'No attack chain'}</h3>
        <p className="text-gray-400 text-sm">{t('vulnerabilities.attackChain.noVulnsDesc') || 'No vulnerability to analyze'}</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('common.overview') || 'Vue d\'ensemble', icon: LayoutGrid },
    { id: 'chains', label: t('vulnerabilities.attackChain.title') || 'Attack chains', icon: Link2, count: rankedHosts.length }
  ];

  return (
    <div className="bg-brand-black rounded-xl border border-brand-green/30 overflow-hidden">
      {/* Compact Header with Tabs */}
      <div className="bg-gradient-to-r from-brand-green/20 to-transparent border-b border-brand-green/20">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-green/20 border border-brand-green/30">
              <Target className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                {t('vulnerabilities.attackChain.analysisTitle') || 'Analyse des Risques'}
                <Sparkles className="w-4 h-4 text-yellow-400" />
              </h2>
              <p className="text-xs text-gray-400">{vulnerabilities.length} {t('vulnerabilities.title') || 'vulnerabilities'} • {stats.totalHosts} {t('vulnerabilities.attackChain.hostsAnalyzed') || 'hosts'}</p>
            </div>
          </div>

          {/* Quick Stats Badges */}
          <div className="hidden md:flex items-center gap-2">
            {stats.criticalPaths > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 border border-red-500/30 rounded-full text-xs text-red-400">
                <Skull className="w-3 h-3" />
                {stats.criticalPaths} {t('vulnerabilities.severities.critical') || 'Critique'}
              </span>
            )}
            {stats.highRiskPaths > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 border border-orange-500/30 rounded-full text-xs text-orange-400">
                <AlertTriangle className="w-3 h-3" />
                {stats.highRiskPaths} {t('vulnerabilities.severities.high') || 'High'}
              </span>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 px-4 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-brand-green/20 text-brand-green border border-brand-green/30' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count && <span className="px-1.5 py-0.5 bg-gray-700 rounded text-xs">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'overview' && (
          <OverviewTab vulnerabilities={vulnerabilities} stats={stats} t={t} />
        )}
        {activeTab === 'chains' && (
          <AttackChainsTab rankedHosts={rankedHosts} onVulnClick={onVulnClick} t={t} />
        )}
      </div>

      {/* Compact Legend */}
      <div className="px-4 py-2 bg-gray-900/50 border-t border-gray-800">
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /> {t('vulnerabilities.attackChain.ratingCritical')}</div>
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-orange-500" /> {t('vulnerabilities.attackChain.ratingHigh')}</div>
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-yellow-500" /> {t('vulnerabilities.attackChain.ratingMedium')}</div>
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500" /> {t('vulnerabilities.attackChain.ratingLow')}</div>
          <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500" /> {t('vulnerabilities.attackChain.ratingSafe')}</div>
        </div>
      </div>
    </div>
  );
};

export default AttackChainRanking;
